import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Calendar,
  CalendarEntryForm, CalendarEntry, CalendarTimeType,
  CalendarViewForm,
  CalendarUpdateEnum,
  CalendarEntryEditTypePicker,
  CalendarViewType,
  getAvailableHours,
} from "mueller-controls";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { 
  addEmployeeCalendarEvent, 
  getCalendarEventsForSignedInUser, 
  updateEmployeeCalendarEvent, 
  deleteEmployeeCalendarEvent, 
  getCalendarEventsByEmployeeId,
  getComplianceForSignedInUser, 
  getComplianceByEmployeeId,
} from "requests/employeeCalendarEvent";
import MSpinner from "components/Shared/MSpinner";
import moment, { Moment } from "moment";
import { getBreakOptions, CalendarBreakVars, getFirstEntryOverlap, BreakOption } from "./CalendarHelpers";
import CalendarBreakSelectionForm from "./CalendarBreakSelectionForm";
import { EmployeeCalendarDeleteRequest, EmployeeComplianceDto, HolidayDto, InspectorServiceTravelRuleResponse } from "interfaces/Mueller.Business.Models";
import { TooltipContext } from "components/Shared/TooltipProvider";
import { getHolidaysBetweenDates } from "requests/holiday";
import { classes } from "components/Utility";
import { Holiday } from "mueller-controls/dist/src/shared/classes";

const monthNumberFormat = "YYYYMM";
const mobileMaxWidth = 550;

export const getHolidayMouseoverText = (holiday: HolidayDto) => `Mueller Holiday: ${holiday.name}`;

export type EmployeeCalendarDeleteRequestMoment = Omit<EmployeeCalendarDeleteRequest, 'dateToUpdate'> & { dateToUpdate: Moment; };

const EmployeeCalendar = props => {
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [monthsLoaded, setMonthsLoaded] = useState([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [isMobile, setIsMobile] = useState(document.body.offsetWidth < mobileMaxWidth);
  const [availableHours, setAvailableHours] = useState(0);
  const [holidays, setHolidays] = useState<HolidayDto[]>();
  const [complianceInfo, setComplianceInfo] = useState<EmployeeComplianceDto>();

  const mounted = useRef(true);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const tooltip = useContext(TooltipContext);
  
  const getMonthNumber = momentDate => parseInt(momentDate.format(monthNumberFormat));

  const maxMonth = getMonthNumber(moment());

  // set the new entries in state, combine with any old entries that we didnt get fresh data for
  const combineEntries = serverEntries => {
    setEntries([
      ...serverEntries,
      ...entries.filter(e => !serverEntries.some(se => se.timeType === e.timeType && se.id === e.id)),
    ]);
  };

  const getCalendarEntries = (startDate?: Moment, endDate?: Moment, fullRefresh: boolean = false) => {
    if (!startDate) startDate = moment().startOf("month").startOf("week"); // start of the week in case this is the month view, it will show some days on the previous month but will never go back further than the last week of the previous month

    // i was going to do this in a useEffect after entries load, but then it would just take longer for no reason.
    // every action that modifies the calendar eventually calls this getCalendarEntries call to update the calendar, so this is a good way to go
    updateCompliance();
    
    if (fullRefresh) {
      if (monthsLoaded.length > 0) {
        let earliestMonthLoaded = monthsLoaded.reduce((acc, month) => month < acc ? month : acc, monthsLoaded[0]);
        startDate = moment(earliestMonthLoaded, monthNumberFormat);
      } else {
        startDate = moment().startOf("month").startOf("week");
      }
      endDate = null;
    }

    const postEntryLoad = entries => {
      if (fullRefresh) setEntries(entries);
      else combineEntries(entries);
      
      setIsLoadingEntries(false);
    };

    setIsLoadingEntries(true);
    if (props.employeeId) {
      getCalendarEventsByEmployeeId(props.employeeId, startDate, endDate, postEntryLoad, null, mounted);
    } else {
      getCalendarEventsForSignedInUser(startDate, endDate, postEntryLoad, null, mounted);
    }
  };

  // useCallback so removeEventListener will have the same function reference and work properly
  const onBodySizeChange = useCallback(() => {
    setIsMobile(document.body.offsetWidth < mobileMaxWidth);
  }, []);

  useEffect(() => {
    const holidayLoadRangeYears = 10;
    getHolidaysBetweenDates(moment().subtract(holidayLoadRangeYears, "year").toDate(), moment().add(holidayLoadRangeYears, "year").toDate(), setHolidays, mounted);
    getCalendarEntries();
    window.addEventListener("resize", onBodySizeChange);

    return () => {
      window.removeEventListener("resize", onBodySizeChange);
    };
  }, []);

  const updateCompliance = () => !!props.employeeId ? getComplianceByEmployeeId(props.employeeId, setComplianceInfo, null, mounted) : getComplianceForSignedInUser(setComplianceInfo, null, mounted);

  // if a callback errors it likely just failed so lets just say we stopped loading and count our current data as valid
  const onRequestError = () => {
    setIsLoadingEntries(false);
  };

  const mapEntryToPostData = (entry: CalendarEntry, isNewEntry?: boolean, editType?: CalendarUpdateEnum, date?: Moment) => {
    let serverEntry = {
      calendarEventType: entry.timeType.id,
      description: entry.description,
      startDateTime: entry.startDate as Moment,
      endDateTime: entry.endDate as Moment,
      recursUntilDate: entry.recursUntilDate,
      recursOn: entry.recursOn,
      employeeId: props.allowManagementEdit ? props.employeeId : null,
    };

    if (!isNewEntry) {
      (serverEntry as any).id = entry.id;
      (serverEntry as any).updateType = editType?.id;
      (serverEntry as any).dateToUpdate = date;
    }

    return serverEntry;
  };

  const onSaveEntryClicked = (entry: CalendarEntry, isNewEntry: boolean, editType: CalendarUpdateEnum, date: Moment) => {
    let entriesToCheck = entries.filter(e => e.timeType === CalendarTimeType.Available && e.id !== entry.id);
    let overlap = entry.timeType === CalendarTimeType.Available ? getFirstEntryOverlap(entry, CalendarBreakVars.breakLengthHours, entriesToCheck) : null;

    if (overlap?.foundOverlappingEntry) {
      dialog.showOk(
        "Error Saving Event",
        `The event you are trying to save overlaps with another event on ${overlap.onDate.format("MM/DD/YYYY")}`,
        () => editEntry(entry, isNewEntry, date, editType)
      );
    } else {
      setIsLoadingEntries(true);
      dialog.hide();

      let addUpdateApiCall = (isNewEntry ? addEmployeeCalendarEvent : updateEmployeeCalendarEvent);

      let breakOptions = null;
      if (entry.timeType === CalendarTimeType.Available) {
        breakOptions = getBreakOptions(entry);
      }

      if (breakOptions && breakOptions.breakRequired) {
        const onSave = (selectedBreak: BreakOption) => {
          // need to pass isNewEntry and editType and date here in case we are adding a break to an existing event, and just editing part of it
          let postData1 = mapEntryToPostData(selectedBreak.startEvent, isNewEntry, editType, date);
          // postData2 and 3 are always a new event
          let postData2 = mapEntryToPostData(selectedBreak.endEvent, true);
          let postData3 = mapEntryToPostData(selectedBreak.breakEvent, true);

          if (entry.isRecurring && !isNewEntry) {
            if (editType !== CalendarUpdateEnum.EntireEvent) {
              postData2.startDateTime = moment(`${date.format("YYYY-MM-DD")} ${postData2.startDateTime.format("HH:mm:ss")}`);
              postData2.endDateTime = moment(`${date.format("YYYY-MM-DD")} ${postData2.endDateTime.format("HH:mm:ss")}`);
              postData3.startDateTime = moment(`${date.format("YYYY-MM-DD")} ${postData3.startDateTime.format("HH:mm:ss")}`);
              postData3.endDateTime = moment(`${date.format("YYYY-MM-DD")} ${postData3.endDateTime.format("HH:mm:ss")}`);
            }

            if (editType === CalendarUpdateEnum.SingleDay) {
              postData2.recursUntilDate = null;
              postData2.recursOn = null;
              postData3.recursUntilDate = null;
              postData3.recursOn = null;
            }
          }

          dialog.hide();
          
          // sequentially add the 3 events for now... it would be a bit hard to run them all at the same time
          addUpdateApiCall(postData1, () => {
            addEmployeeCalendarEvent(postData2, () => {
              addEmployeeCalendarEvent(postData3, getCalendarEntries, onRequestError, mounted);
            }, onRequestError, mounted);
          }, onRequestError, mounted);
        };

        dialog.show(null, <CalendarBreakSelectionForm breakOptions={breakOptions.breakOptions} onSaveClicked={onSave} />);
      } else {
        let postData = mapEntryToPostData(entry, isNewEntry, editType, date);

        addUpdateApiCall(postData, getCalendarEntries, onRequestError, mounted);
      }
    }
  };

  const deleteBreaksTouchingEvent = async (deleteEntryRequest: EmployeeCalendarDeleteRequestMoment, entry: CalendarEntry) => {
    const breaksToDelete = entries.filter(e => {
      // only look at Break‐type entries
      if (e.timeType !== CalendarTimeType.Break) return false;

      if (e.recursUntilDate && moment(e.recursUntilDate).isBefore(moment().startOf("day"))) return false;

      // match on minutes: 
      // either the breaks start == deletedEntry.end, or its end == deletedEntry.start
      let matchesTime = e.startDate.format("HH:mm") === entry.endDate.format("HH:mm") || e.endDate.format("HH:mm") === entry.startDate.format("HH:mm");

      if (!matchesTime) return false;

      // check if events overlap
      const breakStart = e.startDate.clone().startOf("day");
      const breakEnd = e.recursUntilDate
        ? e.recursUntilDate.clone().startOf("day")
        : breakStart;

      // .id here because the update type is the id, not the enum reference
      const deletedStart = (deleteEntryRequest.updateType === CalendarUpdateEnum.SingleDay.id || deleteEntryRequest.updateType === CalendarUpdateEnum.SingleDayAndFuture.id)
                            ?
                            deleteEntryRequest.dateToUpdate.clone().startOf("day")
                            :
                            entry.startDate.clone().startOf("day");
      
      const deletedEnd = entry.recursUntilDate ? entry.recursUntilDate.clone().startOf("day") : deletedStart;

      // @ts-ignore moment mismatch between eminent tech and mueller controls
      return breakStart.isSameOrBefore(deletedEnd) && deletedStart.isSameOrBefore(breakEnd);
    });

    await Promise.all(
      breaksToDelete.map(b => {
        const breakDeleteReq = { ...deleteEntryRequest, id: b.id };
        if (breakDeleteReq.dateToUpdate.isBefore(b.startDate)) breakDeleteReq.dateToUpdate = b.startDate;

        return deleteEmployeeCalendarEvent(breakDeleteReq, null, onRequestError, mounted);
      })
    );
  };

  const deleteEntry = (entry, date, editType?: CalendarUpdateEnum) => {
    if (!editType) {
      if (entry.isRecurring) {
        dialog.showOk("What part of this recurring event would you like to delete?",
          <CalendarEntryEditTypePicker onTypeSelected={type => deleteEntry(entry, date, type)} />
          , null, "Cancel");

        return;
      } else {
        editType = CalendarUpdateEnum.EntireEvent;
      }
    }

    const doTheDelete = async (deleteEntryRequest: EmployeeCalendarDeleteRequestMoment, entry: CalendarEntry) => {
      dialog.hide();
      setIsLoadingEntries(true);
      await deleteEmployeeCalendarEvent(deleteEntryRequest, null, onRequestError, mounted);

      await deleteBreaksTouchingEvent(deleteEntryRequest, entry);
      getCalendarEntries(null, null, true);
    };

    const sureToDeleteClicked = () => {
      let deleteRequest = {
        id: entry.id,
        updateType: editType.id,
        dateToUpdate: date,
        employeeId: props.allowManagementEdit ? props.employeeId : null,
      };

      doTheDelete(deleteRequest, entry);
    };

    dialog.showYesNo(null, "Are you sure you want to delete this entry?", sureToDeleteClicked);
  };

  const editEntry = (entry: CalendarEntry, isNewEntry: boolean, date?: Moment, editType?: CalendarUpdateEnum) => {
    entry = entry.clone(); // clone the entry to prevent the display from updating weird when we edit

    const showEditDialog = editType => {
      if (editType === CalendarUpdateEnum.SingleDay || editType === CalendarUpdateEnum.SingleDayAndFuture) {
        // if we are editing just one part of an event, change the start date and time to the day that we are editing
        // @ts-ignore i think we have a moment library mismatch between eminent tech and mueller react controls?
        entry.startDate = date.clone().set({ "hours": entry.startDate.hours(), "minutes": entry.startDate.minutes() });
        // @ts-ignore i think we have a moment library mismatch between eminent tech and mueller react controls?
        entry.endDate = date.clone().set({ "hours": entry.endDate.hours(), "minutes": entry.endDate.minutes() });
      }

      // if we are just editing a single day, it should no longer be recurring
      if (editType === CalendarUpdateEnum.SingleDay) {
        entry.recursUntilDate = null;
        entry.recursOn = null;
      }

      if (!isNewEntry && [CalendarTimeType.AppraisalAppointment.id, CalendarTimeType.FieldInspectionAppointment.id].includes(entry.timeType.id)) {
        dialog.show(null,
          <CalendarViewForm
            entry={entry}
            onCloseClicked={() => dialog.hide()}
          />,
          null,
          { onHide: () => dialog.hide(), className: "calendar-dialog" });
      } else {
        dialog.show(null,
          <CalendarEntryForm
            entry={entry}
            onSaveClicked={entry => onSaveEntryClicked(entry, isNewEntry, editType, date)}
            onCancelClicked={() => dialog.hide()}
            onDeleteClicked={!isNewEntry && (() => deleteEntry(entry, date, editType))}
            entrySizeLimits={[{ timeType: CalendarTimeType.Available, minimumEntrySizeHours: CalendarBreakVars.minEntryLengthHours, maximumEntrySizeHours: CalendarBreakVars.maxEntryLengthHours }]}
          />,
          null,
          { className: "calendar-dialog" });
      }
    };

    if (editType) {
      showEditDialog(editType);
    } else if (entry.isRecurring) {
      dialog.showOk("What part of this recurring event would you like to edit?", <CalendarEntryEditTypePicker onTypeSelected={type => showEditDialog(type)} />, null, "Cancel");
    } else {
      showEditDialog(CalendarUpdateEnum.EntireEvent);
    }
  };


  const onCalendarViewChanged = (viewType, curCalDate, visibleEntries) => {
    let startOfMonth = curCalDate.clone().startOf("month");
    let endOfMonth = curCalDate.clone().endOf("month");
    let monthNum = getMonthNumber(startOfMonth);

    if (viewType === CalendarViewType.Month) {
      visibleEntries = visibleEntries.filter(e => e.startDate.isBetween(startOfMonth, endOfMonth));
    }

    setAvailableHours(getAvailableHours(visibleEntries, holidays as any));

    if (monthNum < maxMonth && !monthsLoaded.some(m => m === monthNum)) {
      setMonthsLoaded([...monthsLoaded, monthNum]);

      getCalendarEntries(startOfMonth, endOfMonth);
    }
  };

  const onCalendarClick = (date) => {
    let entry = new CalendarEntry({
      timeType: CalendarTimeType.Available,
      startDate: date,
      endDate: date.clone().add(1, "hour"),
    } as CalendarEntry);
    editEntry(entry, true);
  };

  // const onCalendarHeaderClicked = (date: moment.Moment, view: CalendarViewType, holiday: Holiday) => {
  //   if (holiday) return;

  //   let rule: InspectorServiceTravelRuleResponse = {
  //     name: "",
  //     startDate: date.toDate(),
  //   } as InspectorServiceTravelRuleResponse;

    
  // };

  const handleCalendarMouseOver = (date: Moment, view: CalendarViewType, holiday: HolidayDto, isMouseOut: boolean) => {
    if (isMouseOut) tooltip.hide();
    else {
      if (holiday) {
        tooltip.show(getHolidayMouseoverText(holiday));
      }
    }
  };

  let isLoading = isLoadingEntries || !holidays;

  // Explicit check for false because if its null (not loaded) we dont want to show the message
  let showComplianceMessage = complianceInfo?.isInCompliance === false;
  
  return (<div className="employee-calendar">
    <div className="compliance-requirements">{!!showComplianceMessage && complianceInfo.outOfComplianceMessage}</div>
    <div className="additional-employee-info">
      {complianceInfo?.nextDaysAvailabilityMessage && <div className={classes("compliance-availability", showComplianceMessage && "out-of-compliance")}>{complianceInfo.nextDaysAvailabilityMessage}</div>}
      <div>Total Available Hours: {availableHours}</div>
    </div>
    <Calendar
      isLoading={isLoading}
      loadingControl={<MSpinner />}
      calendarEntries={entries}
      onEntryClicked={props.readOnly ? () => {} : (entry, date) => editEntry(entry, false, date)}
      onEntryDeleted={props.readOnly ? null : deleteEntry}
      onCalendarClick={props.readOnly ? () => {} : onCalendarClick}
      onCalendarMouseOver={handleCalendarMouseOver as any}
      onCalendarDragCreateEntryComplete={props.readOnly ? () => {} : entry => editEntry(entry, true)}
      onCalendarViewChanged={onCalendarViewChanged}
      // onCalendarDayHeaderClick={props.readOnly ? () => {} : onCalendarHeaderClicked}
      initialViewType={isMobile ? CalendarViewType.StackedWeek : CalendarViewType.Week}
      blockClickingOnTimeTypes={[CalendarTimeType.Break]}
      blockDeletingOnTimeTypes={[CalendarTimeType.FieldInspectionAppointment, CalendarTimeType.AppraisalAppointment, CalendarTimeType.Break]}
      blockHoveringOnTimeTypes={[CalendarTimeType.Break]}
      scrollCalendarToShowFullDay
      viewTypeOptions={isMobile ? [CalendarViewType.Day, CalendarViewType.StackedWeek] : [CalendarViewType.WorkWeek, CalendarViewType.Week, CalendarViewType.Month]}
      holidays={holidays as any}
    />
  </div>);
};

EmployeeCalendar.propTypes = {
  employeeId: PropTypes.number,
  readOnly: PropTypes.bool,
  allowManagementEdit: PropTypes.bool,  // Allow calendar to be edited on behalf of another user
  appraiser: PropTypes.object,
};

const mapStateToProps = state => {
  return {
    appraiser: state.appraiser.appraiserProfile,
  };
};

export default connect(mapStateToProps)(EmployeeCalendar);
